import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function TextHuntGame({ user }) {
  const { texthuntId } = useParams();
  const navigate = useNavigate();
  
  const [texthunt, setTexthunt] = useState(null);
  const [shuffledQuestions, setShuffledQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [currentAnswer, setCurrentAnswer] = useState('');
  const [selectedOption, setSelectedOption] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [questionStartTime, setQuestionStartTime] = useState(Date.now());
  const [gameFinished, setGameFinished] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTexthunt();
  }, [texthuntId]);

  useEffect(() => {
    if (shuffledQuestions.length > 0 && currentQuestionIndex < shuffledQuestions.length) {
      const currentQ = shuffledQuestions[currentQuestionIndex];
      setTimeLeft(currentQ.time_limit);
      setQuestionStartTime(Date.now());
      setCurrentAnswer('');
      setSelectedOption(null);
    }
  }, [currentQuestionIndex, shuffledQuestions]);

  useEffect(() => {
    if (timeLeft > 0 && !gameFinished) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && shuffledQuestions.length > 0 && !gameFinished) {
      handleNextQuestion();
    }
  }, [timeLeft, gameFinished]);

  const loadTexthunt = async () => {
    try {
      // Check if student already has a graded result
      const checkResponse = await axios.get(`${BACKEND_URL}/api/texthunt/${texthuntId}/result/${user.id}`);
      
      if (checkResponse.data) {
        const result = checkResponse.data;
        
        // Check if graded
        let isGraded = true;
        for (const answer of result.answers) {
          // Check if any short answer is ungraded
          const question = result.texthunt?.questions?.find(q => q.question_id === answer.question_id);
          if (question?.question_type === 'short_answer' && 
              answer.ai_score === null && answer.manual_score === null) {
            isGraded = false;
            break;
          }
        }
        
        if (isGraded) {
          // Already graded, redirect to results
          alert('Este TextHunt ya ha sido corregido. No puedes volver a jugarlo.');
          navigate(`/texthunt/${texthuntId}/result/${user.id}`);
          return;
        }
      }
    } catch (error) {
      // No result yet or error, continue to load game
      console.log('No previous result or not graded yet');
    }
    
    try {
      const response = await axios.get(`${BACKEND_URL}/api/texthunt/${texthuntId}`);
      const data = response.data;
      setTexthunt(data);
      
      // Shuffle questions
      const shuffled = [...data.questions].sort(() => Math.random() - 0.5);
      setShuffledQuestions(shuffled);
      
      setLoading(false);
    } catch (error) {
      alert('Error al cargar TextHunt: ' + (error.response?.data?.detail || error.message));
      navigate('/dashboard');
    }
  };

  const handleNextQuestion = () => {
    const currentQ = shuffledQuestions[currentQuestionIndex];
    const timeTaken = Math.floor((Date.now() - questionStartTime) / 1000);
    
    // Save answer
    const answer = {
      question_id: currentQ.question_id,
      answer: currentQ.question_type === 'multiple_choice' ? String(selectedOption) : currentAnswer,
      time_taken: timeTaken
    };
    
    const newAnswers = [...answers, answer];
    setAnswers(newAnswers);
    
    // Move to next question or finish
    if (currentQuestionIndex + 1 < shuffledQuestions.length) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      finishGame(newAnswers);
    }
  };

  const finishGame = async (finalAnswers) => {
    setGameFinished(true);
    setLoading(true);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/texthunt/${texthuntId}/submit`, {
        student_id: user.id,
        student_name: user.name,
        answers: finalAnswers,
        question_order: shuffledQuestions.map(q => q.question_id)
      });
      
      // Check if there are any short answer questions
      const hasShortAnswers = texthunt.questions.some(q => q.question_type === 'short_answer');
      
      if (hasShortAnswers) {
        // Redirect to dashboard with message - needs teacher correction
        alert('✅ TextHunt completado!\n\n⏳ Tu profesor revisará las respuestas cortas y podrás ver tus resultados cuando estén corregidas.');
        navigate('/dashboard');
      } else {
        // All multiple choice - show results immediately
        navigate(`/texthunt/${texthuntId}/result/${user.id}`);
      }
    } catch (error) {
      alert('Error al enviar respuestas: ' + (error.response?.data?.detail || error.message));
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
        <div className="bg-white rounded-xl p-8 shadow-2xl">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
            <p className="text-xl font-semibold">Cargando TextHunt...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!texthunt || shuffledQuestions.length === 0) {
    return null;
  }

  const currentQ = shuffledQuestions[currentQuestionIndex];
  const progress = ((currentQuestionIndex) / shuffledQuestions.length) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-500 to-purple-600 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-4 mb-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">{texthunt.title}</h1>
              <p className="text-sm text-gray-600">
                Pregunta {currentQuestionIndex + 1} de {shuffledQuestions.length}
              </p>
            </div>
            <div className="text-center">
              <div className={`text-4xl font-bold ${timeLeft <= 10 ? 'text-red-600 animate-pulse' : 'text-blue-600'}`}>
                ⏱️ {timeLeft}s
              </div>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-4 bg-gray-200 rounded-full h-3">
            <div
              className="bg-gradient-to-r from-blue-500 to-purple-600 h-3 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Texto Base (Siempre Visible a la Izquierda) */}
          <div className="bg-white rounded-xl shadow-lg p-6 lg:sticky lg:top-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center gap-2 mb-4 pb-3 border-b">
              <span className="text-2xl">📖</span>
              <h2 className="text-xl font-bold text-gray-800">Texto de Lectura</h2>
            </div>
            <div className="prose prose-sm max-w-none">
              <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                {texthunt.text_content}
              </p>
            </div>
          </div>

          {/* Pregunta Actual (Derecha) */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                  {currentQ.question_type === 'multiple_choice' ? '☑️ Opción Múltiple' : '📝 Respuesta Corta'}
                </span>
                <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                  {currentQ.points} puntos
                </span>
              </div>
              
              <h3 className="text-2xl font-bold text-gray-800 mb-6">
                {currentQ.question_text}
              </h3>

              {currentQ.question_type === 'multiple_choice' && (
                <div className="space-y-3">
                  {currentQ.options.map((option, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedOption(index)}
                      className={`w-full p-4 text-left rounded-xl border-2 transition-all ${
                        selectedOption === index
                          ? 'border-blue-600 bg-blue-50 shadow-md'
                          : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                          selectedOption === index ? 'border-blue-600 bg-blue-600' : 'border-gray-300'
                        }`}>
                          {selectedOption === index && (
                            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                        <span className="text-lg">{option}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {currentQ.question_type === 'short_answer' && (
                <div>
                  <textarea
                    value={currentAnswer}
                    onChange={(e) => setCurrentAnswer(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                    rows="6"
                    placeholder="Escribe tu respuesta aquí..."
                  />
                  <p className="text-sm text-gray-500 mt-2">
                    💡 Escribe una respuesta completa y bien explicada
                  </p>
                </div>
              )}
            </div>

            <button
              onClick={handleNextQuestion}
              disabled={
                (currentQ.question_type === 'multiple_choice' && selectedOption === null) ||
                (currentQ.question_type === 'short_answer' && currentAnswer.trim() === '')
              }
              className="w-full py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-bold text-lg hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg"
            >
              {currentQuestionIndex + 1 === shuffledQuestions.length ? '✅ Finalizar' : '➡️ Siguiente Pregunta'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TextHuntGame;
